import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { KeyboardArrowDown } from "@mui/icons-material";
import ArrowPopper from "components/app/ArrowPopper";
import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useDetailByKey } from "../hooks";
import { motion } from "framer-motion";
import { Skeleton } from "@mui/material";
import { CIOrgDetail } from "types/portfolio";
import { handleExternalUserColorLogic } from "utils";
import { classHOC } from "components/app/ClassWrapper";

const useStyles = makeStyles(() => ({
  listContainer: {
    maxHeight: "52vh",
    overflowY: "auto",
    scrollbarWidth: "thin", //FF
    "&::-webkit-scrollbar": {
      width: 4,
    },
    paddingRight: 8,
  },
  cardContainer: {
    borderRadius: 6,
    border: "1px solid #DEDEDE",
    marginTop: 8,
  },
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 8,
    padding: "0 12px",
    "&:last-of-type": {
      marginBottom: 8,
    },
  },
  itemLabel: {
    fontSize: 12,
    color: "#666",
    lineHeight: "16px",
    width: 128,
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  itemValue: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "16px",
    color: "#333333",
    display: "flex",
    alignItems: "center",
    marginLeft: 25,
  },
  pointWrapper: {
    height: 16,
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
    marginTop: 8,
    fontWeight: 700,
    fontSize: 12,
    textDecoration: "none",
  },
}));

const ChConfigStatusRenderer: FC<
  Partial<ICellRendererParams> & {
    from?: string;
    field?: string;
    statusField?: string;
  }
> = ({ data, value, node, column, from = "grid", field, statusField }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const colId = from === "grid" ? column?.getColId() : field;

  const chDetailConfig =
    statusField === "foundationalStatus"
      ? [
          { label: "Control Hub Org ID", field: "ciOrgId" },
          { label: "Control Hub Org Name", field: "ciOrgName" },
          { label: "Status", field: statusField },
          { label: "Domain Verification", field: "verifiedDomain" },
          { label: "External Users", field: "chExternalUser" },
          { label: "Domain Claim", field: "claimedDomain" },
          { label: "Auto Assign Template", field: "autoAssignTemplate" },
        ]
      : [
          { label: "Control Hub Org ID", field: "ciOrgId" },
          { label: "Control Hub Org Name", field: "ciOrgName" },
          { label: "Status", field: statusField },
          { label: "SSO", field: "ssoEnabledCH" },
          {
            label: "Directory Connector/Entra/Azure Wizard",
            field: "directoryService",
          },
          { label: "Hybrid Calendar", field: "hybridCalendar" },
        ];

  const transfromField = ["ssoEnabledCH", "directoryService", "hybridCalendar"];

  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: detail } = useDetailByKey(anchorEl ? data?.accountKey : null);

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleTxtColor = (data: CIOrgDetail, label: string, value: any) => {
    switch (label) {
      case "Status":
        if (value === "Not Met") {
          return "#D4371C";
        }
        return "#00AB50";
      case "SSO":
      case "Directory Connector/Entra/Azure Wizard":
      case "Hybrid Calendar":
      case "Domain Claim":
      case "Domain Verification":
        if (value !== null) {
          if (value) {
            return "#00AB50";
          }
          return "#D4371C";
        }
        return "#333";
      case "External Users":
        return handleExternalUserColorLogic(data);
      case "Auto Assign Template":
        if (value) {
          return "#00AB50";
        }
        return "#333";
      default:
        return "#333";
    }
  };

  const handleData = (d: { label: string; field: string | undefined }, data: any) => {
    switch (d.label) {
      case "Domain Verification":
      case "Domain Claim":
        return data?.[d?.field as keyof CIOrgDetail] !== null
          ? data?.[d?.field as keyof CIOrgDetail]
            ? "Completed"
            : "Not Completed"
          : "N/A";
      case "SSO":
      case "Directory Connector/Entra/Azure Wizard":
      case "Hybrid Calendar":
        return data?.[d?.field as keyof CIOrgDetail] !== null
          ? data?.[d?.field as keyof CIOrgDetail]
            ? "Enabled"
            : "Not Enabled"
          : "N/A";
      case "External Users":
        return data?.[d?.field as keyof CIOrgDetail]
          ? data?.[d?.field as keyof CIOrgDetail] > 250
            ? "250+"
            : data?.[d?.field as keyof CIOrgDetail]
          : 0;
      case "Control Hub Org ID":
      case "Control Hub Org Name":
      case "Status":
        return data?.[d?.field as keyof CIOrgDetail] !== null
          ? data?.[d?.field as keyof CIOrgDetail]
          : "N/A";
      case "Auto Assign Template":
        return data?.[d?.field as keyof CIOrgDetail] !== null
          ? data?.[d?.field as keyof CIOrgDetail]
            ? "Yes"
            : "No"
          : "N/A";
    }
  };

  if (node?.isRowPinned()) {
    return <></>;
  }
  const val = value ? value?.toLowerCase() : "N/A";

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: val && from === "grid" ? "pointer" : "default",
          width: "fit-content",
        }}
        ref={rootRef}
        onClick={val && from === "grid" ? handleClick : undefined}
      >
        {val === "not met" ? (
          <span style={{ color: "#D4371C" }}>
            Not Met{" "}
            {from === "detail" ? null : (
              <span style={{ color: "#898989" }}>
                (
                {colId === "ciOrgFoundationalStatus"
                  ? data?.ciOrgFoundationalNum + "/" + data?.ciOrgNum
                  : data?.ciOrgAdvanceNum + "/" + data?.ciOrgNum}
                )
              </span>
            )}
          </span>
        ) : val === "met" ? (
          <span style={{ color: "#00AB50" }}>Met</span>
        ) : (
          <span>N/A</span>
        )}
        {val !== "na" && from === "grid" ? (
          <KeyboardArrowDown style={{ fontSize: "1rem" }} />
        ) : null}
      </div>
      {anchorEl ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right"
          disablePortal={false}
        >
          <div
            ref={popRef}
            className={classes.listContainer}
          >
            <p
              style={{
                // textTransform: "uppercase",
                fontSize: 11,
                color: "#121212",
                fontWeight: 400,
              }}
            >
              CH
              {statusField === "foundationalStatus" ? " Foundational" : " Advanced"} Config Status
              <span style={{ color: "#535759", marginLeft: 5 }}>
                (
                {detail !== undefined ? (
                  (detail?.length ?? 0)
                ) : (
                  <Skeleton
                    width={10}
                    height={8}
                    variant="rectangular"
                    style={{ display: "inline-block" }}
                  />
                )}
                )
              </span>
            </p>
            {detail !== undefined ? (
              detail?.map((item) => (
                <div
                  key={item.ciOrgId}
                  className={classes.cardContainer}
                >
                  <motion.div
                    exit={{ opacity: 0 }}
                    key={item?.ciOrgId}
                  >
                    {chDetailConfig.map((d) => (
                      <div
                        className={classes.itemContainer}
                        key={d.field}
                      >
                        <div className={classes.itemLabel}>{d.label}</div>
                        <div className={classes.itemValue}>
                          <span
                            style={{
                              maxWidth: 200,
                              textTransform: transfromField.includes(d.field as string)
                                ? "capitalize"
                                : "none",
                              textAlign: "right",
                              color: handleTxtColor(
                                item,
                                d.label,
                                item[d?.field as keyof CIOrgDetail]
                              ),
                              // overflow: "hidden",
                              // textOverflow: "ellipsis",
                              // whiteSpace: "nowrap",
                              // color: "#333",
                            }}
                          >
                            {handleData(d, item)}
                          </span>
                        </div>
                      </div>
                    ))}
                    <Divider style={{ marginTop: 14 }} />
                    <div style={{ padding: "6px 14px" }}>
                      <a
                        className={classes.pointWrapper}
                        style={{ display: "flex", alignItems: "center" }}
                        href={`/spng/summary?accountId=${data?.accountId}&subscription=${data?.subscriptionRefId}&section=${data?.subscriptionRefId}ControlHub`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Show Details &gt;
                      </a>
                    </div>
                  </motion.div>
                </div>
              ))
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {Array.from({ length: 4 }).map((x, i) => (
                  <Skeleton
                    key={`${x}${i}`}
                    width={300}
                    height={32}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </div>
            )}
          </div>
        </ArrowPopper>
      ) : null}
    </>
  );
};

export default classHOC(ChConfigStatusRenderer);
